import React, { useEffect } from 'react';
import { V2Layout, V2Button, V1Wrap, HeroDivider } from './V2Shared';
import { GoogleReviews } from '../App';
import SEO, { buildBreadcrumb, SITE_URL } from '../components/SEO';

const STEPS = [
  { num: '01', meta: 'INTAKE', title: 'Send Documents', desc: 'Submit papers and service details via secure form or email. Receipt confirmed within minutes.' },
  { num: '02', meta: 'FIELD WORK', title: 'We Attempt & Log', desc: 'Certified servers execute in the field with real-time GPS logs and detailed attempt notes.' },
  { num: '03', meta: 'PROOF', title: 'Affidavit Delivered', desc: 'Completed affidavit of service delivered immediately — notarized, filed, in your hands.' },
  { num: '04', meta: 'ACCESS', title: 'Portal 24/7', desc: 'Full case history, attempts, invoices and downloads available any time in your Client Portal.' },
];

/* ============== HERO — Document Drop + Logo Stamp + Steps ============== */
const V2Hero = () => {
  const bgRef = React.useRef(null);
  // Subtle parallax — translate skyline as user scrolls
  React.useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        el.style.transform = `translate3d(0, ${y * 0.25}px, 0) scale(${1 + Math.min(y, 600) * 0.0002})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className="v2-hero" data-testid="v2-hero">
      <div
        className="v2-hero-bg"
        aria-hidden="true"
        ref={bgRef}
        style={{ backgroundImage: 'url(/lubbock-skyline.jpg)' }}
      />
      <div className="v2-hero-grain" />

      <div className="v2-hero-body">
        {/* Logo Stamp animation stage */}
        <div className="v2-hero-doc-stage" aria-hidden="true">
          <div className="v2-hero-stamp-ring" />
          <div className="v2-hero-stamp-ring inner" />
          <img src="/westex-logo.png" alt="WesTex Legal" className="v2-hero-logo-reveal" data-testid="v2-hero-logo" />
        </div>

        {/* Supporting CTAs below */}
        <div className="v2-hero-support" data-testid="v2-hero-support">
          <h1 className="v2-hero-support-h1">Process serving in Lubbock, Texas — and all 254 counties.</h1>
          <p className="v2-hero-support-text">
            WesTex Legal is a NAPPS-certified process server based in Lubbock, Texas — serving all 254 Texas counties, all of New Mexico, and nationwide. Real-time case tracking, rigorous affidavits, direct-line communication.
          </p>
          <div className="v2-hero-support-ctas">
            <V2Button to="/job-request" testId="v2-hero-cta-primary">Submit Job</V2Button>
            <V2Button to="/areas-pricing" variant="ghost" testId="v2-hero-cta-secondary">Coverage & Pricing</V2Button>
          </div>
        </div>

        {/* Steps — over the Lubbock skyline */}
        <div className="v2-hero-steps" data-testid="v2-hero-steps">
          {STEPS.map(s => (
            <div key={s.num} className="v2-step v2-step-glass">
              <div className="v2-step-num">{s.num}</div>
              <div className="v2-step-meta">{s.meta}</div>
              <h3 className="v2-step-title">{s.title}</h3>
              <p className="v2-step-desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============== REAL GOOGLE REVIEWS — shuffled + auto-rotating ============== */
const ReviewsSection = () => {
  const [pool, setPool] = React.useState([]);
  const [idx, setIdx] = React.useState(0);
  const [fading, setFading] = React.useState(false);
  const [summary, setSummary] = React.useState({ average_rating: null, total_reviews: null });

  // Fetch and shuffle once
  useEffect(() => {
    const fallback = [
      { author_name: 'D. G.', relative_time_description: '5 months ago', text: 'Should have called sooner—quick, professional, and on point.', rating: 5 },
      { author_name: 'Linda Day', relative_time_description: '4 months ago', text: 'I sent the documents and they handled the rest the same day we spoke.', rating: 5 },
      { author_name: 'Fran Green', relative_time_description: '2 months ago', text: 'Great service and flexible to our needs.', rating: 5 },
    ];
    const shuffle = (arr) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };
    fetch('/api/reviews/google')
      .then(r => r.ok ? r.json() : null)
      .then(d => setPool(shuffle(d && d.length ? d : fallback)))
      .catch(() => setPool(shuffle(fallback)));

    fetch('/api/reviews/google/summary')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSummary(d); })
      .catch(() => {});
  }, []);

  // Auto-rotate every 45s with a brief fade — only meaningful if we have more reviews than visible
  useEffect(() => {
    if (pool.length <= 5) return;
    const t = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setIdx(i => (i + 1) % pool.length);
        setFading(false);
      }, 400);
    }, 45000);
    return () => clearInterval(t);
  }, [pool.length]);

  const visible = React.useMemo(() => {
    if (!pool.length) return [];
    const count = Math.min(5, pool.length);
    return Array.from({ length: count }, (_, o) => pool[(idx + o) % pool.length]);
  }, [pool, idx]);

  return (
    <section className="v2-section v2-section-dark v2-section-tight">
      <div className="v2-container">
        <div className="v2-sect-header v2-sect-header-centered">
          {(() => {
            const rating = summary.average_rating ? Number(summary.average_rating).toFixed(1) : '5.0';
            const countText = summary.total_reviews ? ` · ${summary.total_reviews} reviews` : '';
            const tagContent = (
              <>
                Google Reviews · <span className="v2-reviews-rating">★ {rating}</span>{countText}
              </>
            );
            return summary.review_url ? (
              <a
                className="v2-mono-tag v2-reviews-link"
                href={summary.review_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="v2-reviews-summary"
                aria-label={`View all ${summary.total_reviews || ''} Google reviews for WesTex Legal`}
              >
                {tagContent}
              </a>
            ) : (
              <div className="v2-mono-tag" data-testid="v2-reviews-summary">
                {tagContent}
              </div>
            );
          })()}
          <h2 className="v2-sect-title v2-sect-title-sm">
            What our clients <em>actually say.</em>
          </h2>
        </div>
      </div>
      {visible.length > 0 && (
        <div className={`v2-notes ${fading ? 'is-fading' : ''}`} data-testid="v2-reviews">
          {visible.map((r, i) => (
            <article key={`${idx}-${i}`} className="v2-note">
              <div className="v2-note-meta">Verified Google Review</div>
              <div className="v2-note-stars">{'★'.repeat(r.rating || 5)}</div>
              <p className="v2-note-text">"{(r.text || '').slice(0, 200)}{r.text && r.text.length > 200 ? '…' : ''}"</p>
              <div className="v2-note-author">
                {r.author_name}
                <small>{r.relative_time_description}</small>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

/* ============== HOME ============== */
export default function V2Home() {
  return (
    <V2Layout>
      <SEO
        title="WesTex Legal — Process Servers & Mobile Notary | Lubbock, TX"
        description="Texas & New Mexico process servers and mobile notary services. JBCC-certified, NAPPS members. 24/7 availability, GPS-tracked attempts, same-day affidavits. Call (806) 401-1749."
        path="/"
        image={`${SITE_URL}/lubbock-skyline.jpg`}
        schema={buildBreadcrumb([{ name: 'Home', path: '/' }])}
      />
      <V2Hero />
      <HeroDivider />
      <ReviewsSection />
      {/* Keep V1 hidden, reviews rendered above instead */}
      <div style={{ display: 'none' }}>
        <V1Wrap><GoogleReviews /></V1Wrap>
      </div>
    </V2Layout>
  );
}
